import { useLocation } from 'react-router-dom';
import { formatCurrency } from '../../utils/datetime';
import {
  KIND_LABELS,
  METHOD_LABELS,
  PAYOUT_LABELS,
  useWorkspaceSales,
} from '../../hooks/useWorkspaceSales';
import PayoutStatusCard from './PayoutStatusCard';
import CashOutPanel from './CashOutPanel';

const statusTone = {
  paid_out: 'bg-emerald-50 text-emerald-800',
  split: 'bg-emerald-50 text-emerald-800',
  pending_payout: 'bg-saffron-500/15 text-saffron-800',
  settling: 'bg-ink-900/5 text-ink-700',
  failed: 'bg-red-50 text-red-700',
  collected: 'bg-ink-900/5 text-ink-700',
};

const kindTone = {
  deposit: 'bg-saffron-500/15 text-saffron-800',
  full_payment: 'bg-emerald-50 text-emerald-800',
  balance: 'bg-ink-900/5 text-ink-700',
};

const formatWhen = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SalesTransactions = () => {
  const location = useLocation();
  const { data, loading, error } = useWorkspaceSales();
  const currency = data?.currency || 'GHS';
  const transactions = data?.transactions || [];
  const cashOutSuccess = location.state?.cashOutSuccess;

  return (
    <div className="space-y-6 pb-28 sm:pb-24">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-light text-ink-900">Transactions</h2>
          <p className="mt-1 text-sm text-ink-700/80">
            Deposits hold a booking; full payments settle the visit. Cash out cleared charges to the
            account you connected.
          </p>
          <p className="mt-2 text-sm text-ink-600/70">
            Paid today{' '}
            <span className="font-medium text-ink-900">
              {formatCurrency(data?.paidToday || 0, currency)}
            </span>
          </p>
        </div>
        <CashOutPanel payout={data?.payout} currency={currency} />
      </div>

      {cashOutSuccess && (
        <div className="border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          Cash out of {formatCurrency(location.state?.amount || 0, currency)} is on its way to the
          account you confirmed.
        </div>
      )}

      {error && (
        <div className="border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <PayoutStatusCard payout={data?.payout} currency={currency} loading={loading} />

      <div className="overflow-x-auto bg-sand-50 ring-1 ring-ink-900/10">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-900/20 border-t-saffron-500" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-ink-700/70">
            No payments yet. Deposits appear when a client pays on your booking link; full payments
            appear when the service is settled.
          </p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-ink-900/10 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/55">
              <tr>
                <th className="px-4 py-3 sm:px-5">When</th>
                <th className="px-4 py-3 sm:px-5">Client</th>
                <th className="hidden px-4 py-3 sm:table-cell sm:px-5">Service</th>
                <th className="px-4 py-3 sm:px-5">Type</th>
                <th className="px-4 py-3 sm:px-5">Method</th>
                <th className="px-4 py-3 sm:px-5">Payout</th>
                <th className="px-4 py-3 text-right sm:px-5">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-900/10">
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="whitespace-nowrap px-4 py-3 text-ink-600/70 sm:px-5">
                    {formatWhen(tx.paidAt)}
                  </td>
                  <td className="px-4 py-3 font-medium text-ink-900 sm:px-5">{tx.client}</td>
                  <td className="hidden px-4 py-3 text-ink-700/80 sm:table-cell sm:px-5">
                    {tx.service}
                  </td>
                  <td className="px-4 py-3 sm:px-5">
                    <span
                      className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                        kindTone[tx.kind] || kindTone.deposit
                      }`}
                    >
                      {KIND_LABELS[tx.kind] || tx.kind || 'Deposit'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ink-700/80 sm:px-5">
                    {METHOD_LABELS[tx.method] || tx.methodRaw || tx.method}
                  </td>
                  <td className="px-4 py-3 sm:px-5">
                    <span
                      className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                        statusTone[tx.payoutStatus] || statusTone.collected
                      }`}
                    >
                      {PAYOUT_LABELS[tx.payoutStatus] || tx.payoutStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right sm:px-5">
                    <p className="font-medium tabular-nums text-ink-900">
                      {formatCurrency(tx.amount, tx.currency || currency)}
                    </p>
                    {tx.kind === 'deposit' && tx.remainingDue > 0 && (
                      <p className="mt-0.5 text-[11px] text-ink-600/60">
                        {formatCurrency(tx.remainingDue, tx.currency || currency)} still due
                      </p>
                    )}
                    {tx.source === 'online' && (
                      <p className="mt-0.5 text-[11px] text-ink-600/60">
                        {formatCurrency(tx.payoutAmount || 0, tx.currency || currency)} after fees
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="h-16 sm:h-8" aria-hidden="true" />
    </div>
  );
};

export default SalesTransactions;
